import { useState } from 'react';
import {
  BookOpen,
  ClipboardCheck,
  ClipboardList,
  Droplets,
  HeartPulse,
  LogOut,
  Pencil,
  Pill,
  UserPlus,
} from 'lucide-react';
import { NOTE_TYPE_LABELS } from '../../constants/schemas';
import { getNoteTypeLabel } from '../../utils/noteTypeLabels';
import { useLanguage } from '../../contexts/LanguageContext';
import PatientAvatarStack from './PatientAvatarStack';

const NOTE_TYPE_ICONS = {
  progress_note: BookOpen,
  vitals: HeartPulse,
  medication_admin: Pill,
  fluid_balance: Droplets,
  care_plan: ClipboardCheck,
  handover: ClipboardList,
  admission: UserPlus,
  discharge: LogOut,
};

export default function PatientHeaderBar({
  patient,
  noteTypes = [],
  onPatientChange,
  allowEdit = true,
  patients = null,
  onSelectPatient = null,
}) {
  const { language } = useLanguage();
  const [editing, setEditing] = useState(false);
  const missingDetails = !patient?.name || !patient?.room;
  const multiPatients = Array.isArray(patients) && patients.length > 1;

  const patientLine = [
    patient?.name || 'Unknown Patient',
    patient?.room ? `Room ${patient.room}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (multiPatients) {
    // Avatars on top; detected note-type icons directly underneath (not beside Close).
    return (
      <div className="shrink-0 rounded-t-[24px] border-b border-gray-200 bg-white px-4 py-3 pr-16">
        <div className="min-w-0">
          <PatientAvatarStack
            patients={patients}
            size="md"
            onSelect={onSelectPatient}
          />
          {noteTypes.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {noteTypes.map((type) => {
                const Icon = NOTE_TYPE_ICONS[type] || BookOpen;
                const label =
                  getNoteTypeLabel(type, language) || NOTE_TYPE_LABELS[type] || type;
                return (
                  <span
                    key={type}
                    title={label}
                    aria-label={label}
                    className="inline-flex h-8 w-8 items-center justify-center text-primary"
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 rounded-t-[24px] border-b border-gray-200 bg-white px-4 py-4 pr-16">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-lg font-bold text-gray-900">{patientLine}</p>
          {allowEdit && missingDetails && (
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="shrink-0 text-warning"
              aria-label="Edit patient details"
            >
              <Pencil className="h-5 w-5 text-primary" strokeWidth={2} />
            </button>
          )}
        </div>
        {noteTypes.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {noteTypes.map((type) => {
              const Icon = NOTE_TYPE_ICONS[type] || BookOpen;
              const label =
                getNoteTypeLabel(type, language) || NOTE_TYPE_LABELS[type] || type;
              return (
                <span
                  key={type}
                  title={label}
                  aria-label={label}
                  className="inline-flex h-8 w-8 items-center justify-center text-primary"
                >
                  <Icon className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      {allowEdit && editing && (
        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
          <input
            type="text"
            value={patient?.name || ''}
            onChange={(e) => onPatientChange({ ...patient, name: e.target.value || null })}
            placeholder="Patient name"
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          />
          <input
            type="text"
            value={patient?.room || ''}
            onChange={(e) => onPatientChange({ ...patient, room: e.target.value || null })}
            placeholder="Room number"
            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          />
        </div>
      )}
    </div>
  );
}
